<script setup>
import { use } from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { TooltipComponent, GridComponent, LegendComponent } from 'echarts/components';
import { SVGRenderer } from 'echarts/renderers';
import VChart from 'vue-echarts';
import {onMounted, ref, watch} from 'vue';
import * as echarts from 'echarts/core';
import {detectMinMax, formatYearsString} from '../../helper';
import {getTenantData} from '@/services/TenantService';
import {formatDate, formatAmount, formatNumber, formatPercents, roundMinMaxValues} from '../../helper';

use([ TooltipComponent, GridComponent, LineChart, LegendComponent, SVGRenderer]);

const theme = ref({});

const maxVisibleCategories = 5;
const maxVisibleYAxisLines = 3;


const formatChartTooltipDateAndNumberOrAmount = (params) => {

    let param = params[0];
    let string = '';

  if(props.categoriesType === 'number') {
    string += param.axisValue;
  } if(props.categoriesType === 'years') {
    string += formatYearsString(param.axisValue);
  }  else {
          string += formatDate(param.axisValue, 'd.m.Y');
        }

      params.forEach(function(paramItem) {
          string += '<br/>' + paramItem.marker + ' ' + paramItem.seriesName + ': ';
          if(props.valuesType === 'amount') {
            string += formatAmount(paramItem.value, props.currency, props.digits);
          } else {
            string += formatNumber(paramItem.value);
          }
      });

      return string;

}

const defaultSerie = {
  name: '',
  type: 'line',
  smooth: true,
  symbol: 'none',
  data: []
}

let prepared = ref(false);

const props = defineProps({
  series: Array,
  categories: Array,
  valuesType: 'number' | 'amount',
  categoriesType: 'number' | 'date' | 'years',
  currency: 'CZK' | 'EUR' | null,
  view: 'minimal' | null,
  showAreaBackground: true | false,
  digits: 2
})

watch(() => props.series, (first, second) => {
    prepareChartData();
});

const interval = Math.ceil(props.categories.length / maxVisibleCategories);

const defaultOptions = ref({
  legend: {
    orient: 'horizontal',
    bottom: 0,
    left: 'center'
  },
  tooltip: {
    trigger: 'axis',
  },

  xAxis: {
    type: 'category',
    axisTick: {
      show: false,
    },
    axisLine: {
      show: false
    },
    axisLabel: {
      align: 'left',
      interval: interval,
      color: 'rgba(0,65,72,.5)',
      formatter: function (value, index) {
        if(props.categoriesType === 'number' || props.categoriesType === 'years') {
          return value;
        }
        return formatDate(value, 'd.m.Y');
      }
    }
  },
  yAxis: {
    type: 'value',
    position: 'left',
    scale: true,
    splitLine: {
      show: true,
    },
    axisLine: {
      onZero: false
    },
    axisLabel: {
      inside: true,
      showMinLabel: false,
      showMaxLabel: true,
      verticalAlign: 'top',
      interval: 20,
      color: 'rgba(0,65,72,.5)',
      formatter: function(value, index) {
        if(props.valuesType === 'amount') {
          return formatAmount(value, props.currency, props.digits);
        } else {
          return formatNumber(value);
        }
      }
    },
    axisTick: {
      show: false
    },
  },
  grid: {
    left: '0',
    right: 0,
    top: 0,
    bottom: '66px',
    width: 'auto',
    height: 'auto',
    show: false
  },
  series: []
});

defaultOptions.value['tooltip']['formatter'] = function(params) {
  return formatChartTooltipDateAndNumberOrAmount(params);
};

const defaultOptionsMinimal = ref({
  tooltip: {
    trigger: 'axis',
  },

  xAxis: {
    type: 'time',
    boundaryGap: false
  },
  yAxis: {
    type: 'value',
    boundaryGap: [0, '100%'],
    splitLine: {
      show: false
    },
  },
  grid: {
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    width: 'auto',
    height: 'auto',
    show: false
  },
  series: []
});

defaultOptionsMinimal.value['tooltip']['formatter'] = function(params) {
  return formatChartTooltipDateAndNumberOrAmount(params);
};

const options = ref({});


onMounted(() => {
  getTenantData()
      .then((response) => {
        theme.value = response.theme;
        prepareChartData();
      });
});

function prepareChartData() {
  let preparedSeries = [];
  let min = 9999999999;
  let max = -999999999;
  let tmpMinMax = null;

  // loop through series and prepare them
  props.series.forEach(serie => {

    // prepare serie to wanted format
    preparedSeries.push(prepareSerie(serie));

    // detect min value
    tmpMinMax = detectMinMax(serie.data);
    if(tmpMinMax[0] < min) {
      min = tmpMinMax[0];
    }
    if(tmpMinMax[1] > max) {
      max = tmpMinMax[1];
    }
  });

  options.value = props.view === 'minimal' ? defaultOptionsMinimal.value : defaultOptions.value;
  options.value.interval = undefined;

  // refactor min a max - round to rounded values (to 100 or 1000)
  let rounded = roundMinMaxValues(min, max)
  min = rounded.min;
  max = rounded.max;


  // update options with series, min value and max value
  options.value.series = preparedSeries;
  options.value.yAxis.min = min;
  options.value.yAxis.max = max;
  const interval = rounded.step ?? (max - min) / maxVisibleYAxisLines;
  if (interval > 0) {
    options.value.yAxis.interval = interval;
  }

  if(props.categories && props.view !== 'minimal')
    options.value.xAxis.data = props.categories;



  // set switch that data are prepared
  prepared.value = true;
};


/**
 * prepare serie, merge with default etc...
 */
function prepareSerie(serie) {
  let preparedSerie = {...defaultSerie, ...serie};

  // prepare item style
  if(!preparedSerie.itemStyle) {
    preparedSerie.itemStyle = {
      color: theme.value.primaryColor ? theme.value.primaryColor : 'rgb(20, 55, 180)'
    };
  }

  let usedColor = preparedSerie.itemStyle.color;

  const getColorWithAlpha = (color, alpha) => {
    if (typeof color !== 'string') {
      return color;
    }

    const match = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([\d.]+))?\s*\)/i);
    if (match) {
      const [, r, g, b] = match;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    if (color.startsWith('#')) {
      const hex = color.slice(1);
      const normalized = hex.length === 3
        ? hex.split('').map((ch) => ch + ch).join('')
        : hex;

      if (normalized.length === 6) {
        const r = parseInt(normalized.slice(0, 2), 16);
        const g = parseInt(normalized.slice(2, 4), 16);
        const b = parseInt(normalized.slice(4, 6), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
      }
    }

    return color;
  };

  const colorAt25 = getColorWithAlpha(usedColor, 0.65);
  const colorAt50 = getColorWithAlpha(usedColor, 0.35);
  const colorAt75 = getColorWithAlpha(usedColor, 0.1);
  let colorAt100 = getColorWithAlpha(usedColor, 0);

  if (colorAt100 === usedColor) {
    colorAt100 = 'rgba(255, 255, 255, 0)';
  }

  // prepare area style
  if(!preparedSerie.areaStyle && (typeof props.showAreaBackground === 'undefined' || props.showAreaBackground === true)) {
    preparedSerie.areaStyle = {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: usedColor
        },
        {
          offset: 0.25,
          color: colorAt25
        },
        {
          offset: 0.5,
          color: colorAt50
        },
        {
          offset: 0.75,
          color: colorAt75
        },
        {
          offset: 1,
          color: colorAt100
        }
      ])
    };
  }

  return preparedSerie;
}

</script>
<template>
  <v-chart v-if="prepared" class="chart" :option="options" autoresize />
</template>
<style scoped>
/* .chart {
  height: 200px;
} */
</style>
